import { createClient } from '@supabase/supabase-js';
import { encryptText, decryptText } from './encryptionService';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Missing Supabase URL or anon key in .env.local');
}

// Keep minimal client for real-time subscriptions only
export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Edge Function base URL
const EDGE_FUNCTIONS_URL = `${supabaseUrl}/functions/v1`;

/**
 * Helper function to make Edge Function requests
 */
const callEdgeFunction = async (functionName, payload = {}) => {
    try {
        const response = await fetch(`${EDGE_FUNCTIONS_URL}/${functionName}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${supabaseAnonKey}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Edge Function error: ${response.status} ${errorText}`);
        }

        return await response.json();
    } catch (error) {
        console.error(`Error calling Edge Function ${functionName}:`, error);
        throw error;
    }
};

/**
 * Create a new clipboard with a short text ID and return it
 */
export const createClipboard = async () => {
    const response = await callEdgeFunction('create-clipboard');
    return response.clipboardId;
};

/**
 * Fetch all items for a clipboard using the text ID
 */
export const fetchClipboardItems = async (clipboardId) => {
    const response = await callEdgeFunction('get-clipboard', { clipboardId });
    const items = response.items || [];

    console.log('Fetched clipboard items (encrypted):', items);
    
    // Decrypt all items
    const decryptedData = await Promise.all(
        items.map(async (item) => {
            try {
                return {
                    ...item,
                    text: await decryptText(item.text, clipboardId)
                };
            } catch (err) {
                console.error('Error decrypting item:', err);
                // Return encrypted text if decryption fails
                return item;
            }
        })
    );
    
    console.log('Decrypted clipboard items:', decryptedData);
    return decryptedData;
};

/**
 * Add a new item to a clipboard (encrypted)
 */
export const addClipboardItem = async (clipboardId, text, timestamp) => {
    try {
        const encryptedText = await encryptText(text, clipboardId);
        
        const response = await callEdgeFunction('add-item', {
            clipboardId,
            text: encryptedText,
            timestamp
        });

        // Return decrypted for display
        return {
            ...response.item,
            text: await decryptText(response.item.text, clipboardId)
        };
    } catch (err) {
        console.error('Error in addClipboardItem:', err);
        throw err;
    }
};

/**
 * Delete an item from a clipboard
 */
export const deleteClipboardItem = async (itemId) => {
    await callEdgeFunction('delete-item', { itemId });
};

/**
 * Update an item in a clipboard (encrypted)
 */
export const updateClipboardItem = async (itemId, text, clipboardId) => {
    try {
        console.log('Updating item with ID:', itemId, 'Text:', text);
        
        const encryptedText = await encryptText(text, clipboardId);
        const newTimestamp = new Date().toLocaleTimeString();
        
        const response = await callEdgeFunction('update-item', {
            itemId,
            text: encryptedText,
            timestamp: newTimestamp
        });

        console.log('Item updated, response:', response);
        
        // Return decrypted for display
        return Promise.all(
            response.items.map(async (item) => ({
                ...item,
                text: await decryptText(item.text, clipboardId)
            }))
        );
    } catch (err) {
        console.error('Error updating clipboard item:', err);
        throw err;
    }
};

/**
 * Subscribe to real-time changes on clipboard items
 */
export const subscribeToClipboardChanges = (clipboardId, callback) => {
    const subscription = supabase
        .from(`clipboard_items:clipboard_id=eq.${clipboardId}`)
        .on('*', (payload) => {
            callback(payload);
        })
        .subscribe();

    return subscription;
};
